import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  styled,
  Typography,
  useTheme,
  Tab,
  Tabs,
  BoxProps,
  TabsProps,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BoltIcon from '@mui/icons-material/Bolt';
import { MonthlyBillChart } from './BillChart';
import {
  BillEstimateResponse, ServicePoint, VisualizerDetailsMonthData, VisualizerMonthData, VisualizerPlanData,
} from '../../models';
import { Visualizer } from '../visualizer/Visualizer';
import { FeatureContext } from '../../FeatureContext';
import { Bold } from '../shared/Styles';

const MonthlyBillChartWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  padding: '30px 0',
  height: '100%',
}));

const MonthlyBillChartHeaderWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  padding: '16px',
  backgroundColor: theme.palette.altBackground.main,
}));

const HeaderDetails = styled(Tabs)<TabsProps>(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.tab.underline,
  },
}));

interface BillEstimateProps {
  billEstimateResponse: BillEstimateResponse[];
  selectedServicePoint: ServicePoint | undefined;
  visualizerOverviewData: VisualizerPlanData<VisualizerMonthData>[] | undefined;
  visualizerDetailsData: VisualizerPlanData<VisualizerDetailsMonthData>[] | undefined;
  visualizerOverviewLoading: boolean;
  visualizerDetailsLoading: boolean;
}

function getBold(chunks: any) {
  return <Bold component="span">{chunks}</Bold>;
}

function MonthlyBillEstimate({
  billEstimateResponse,
  selectedServicePoint,
  visualizerOverviewData,
  visualizerDetailsData,
  visualizerOverviewLoading,
  visualizerDetailsLoading,
}: BillEstimateProps) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const featureContext = useContext(FeatureContext);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  }, []);

  useEffect(() => {
    if (!featureContext.ENERGY_USAGE_VISUALIZER) {
      setActiveTab(0);
    }
  }, [featureContext.ENERGY_USAGE_VISUALIZER]);

  return (
    <MonthlyBillChartWrapper>
      <Grid container direction="column" spacing={3}>
        <Grid item xs={12} sm={12} md={6}>
          {featureContext.ENERGY_USAGE_VISUALIZER ? (
            <Typography variant="h6" color={theme.palette.mainBackground.contrastText}>
              <FormattedMessage
                id="explore-potential-savings"
                values={{
                  bold: (chunks: any) => getBold(chunks),
                }}
              />
            </Typography>
          ) : (
            <Typography variant="h6" color={theme.palette.primary.dark}>
              <FormattedMessage
                id="estimated-monthly-bill.header"
                values={{
                  bold: (chunks: any) => getBold(chunks),
                }}
              />
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Card>
            {featureContext.ENERGY_USAGE_VISUALIZER ? (
              <HeaderDetails value={activeTab} onChange={handleTabChange} indicatorColor="secondary" textColor="inherit">
                <Tab
                  icon={<AttachMoneyIcon />}
                  label={(
                    <FormattedMessage
                      id="estimated-monthly-bill.tab"
                    />
                  )}
                />
                <Tab
                  icon={<BoltIcon />}
                  label={(
                    <FormattedMessage
                      id="review-energy-usage"
                    />
                  )}
                />
              </HeaderDetails>
            ) : (
              <MonthlyBillChartHeaderWrapper color={theme.palette.bluePlan.contrastText}>
                <Typography variant="h6">
                  <FormattedMessage id="monthly-bill-header" />
                </Typography>
              </MonthlyBillChartHeaderWrapper>
            )}
            {activeTab === 0 && (
              <CardContent>
                {billEstimateResponse && <MonthlyBillChart resp={billEstimateResponse} selectedServicePoint={selectedServicePoint} />}
              </CardContent>
            )}
            {activeTab === 1 && (
              <Visualizer
                visualizerOverviewData={visualizerOverviewData}
                visualizerDetailsData={visualizerDetailsData}
                visualizerOverviewLoading={visualizerOverviewLoading}
                visualizerDetailsLoading={visualizerDetailsLoading}
              />
            )}
          </Card>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Typography variant="body2">
            <FormattedMessage
              id="rate-plan-note.p1"
              values={{
                bold: (chunks: any) => getBold(chunks),
              }}
            />
          </Typography>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Typography variant="body2">
            <FormattedMessage
              id="rate-plan-note.p3"
              values={{
                bold: (chunks: any) => getBold(chunks),
              }}
            />
          </Typography>
        </Grid>
      </Grid>
    </MonthlyBillChartWrapper>
  );
}

export { MonthlyBillEstimate };
